/**
 * OAuth Gate Component
 *
 * Wrapper component that ensures user has valid OAuth token before
 * allowing access to the application. This is Layer 1 of authentication.
 *
 * States:
 * - Loading: Shows spinner while validating token
 * - Validated: Renders children (app content)
 * - Not validated: Redirects to OAuth or shows error
 */

import React, { useEffect, useState } from 'react';
import { useOAuth } from '../hooks/useOAuth';
import { useLanguage } from '../i18n/LanguageContext';
import { OAUTH_ENABLED } from '../config/oauth';
import { RefreshCw, AlertCircle, ShieldOff } from 'lucide-react';
import { HTE_ACCESS_ENFORCE } from '../config/oauth';

interface OAuthGateProps {
  children: React.ReactNode;
}

const OAuthGate: React.FC<OAuthGateProps> = ({ children }) => {
  const { isOAuthValidated, isOAuthLoading, oauthError, hasHteAccess, oauthUser, startOAuth, clearOAuthAndRestart, logout } = useOAuth();
  const { t } = useLanguage();
  // WCO-2914: set ONLY by "Sign out" on the access-denied screen. Without it, logout() leaves the
  // gate unvalidated with no error, the auto-redirect below re-runs startOAuth(), silent SSO signs
  // the same account straight back in, and the denied user can never leave. Deliberately local to
  // this screen: an ordinary sign-out (useAuth → logout) keeps auto-returning to sign-in.
  const [deniedSignedOut, setDeniedSignedOut] = useState(false);

  // If OAuth is disabled (for local development), just render children
  if (!OAUTH_ENABLED) {
    return <>{children}</>;
  }

  // Auto-redirect to OAuth if not validated and not loading and no error — except after a sign-out
  // from the access-denied screen (see deniedSignedOut above).
  useEffect(() => {
    if (!isOAuthLoading && !isOAuthValidated && !oauthError && !deniedSignedOut) {
      startOAuth();
    }
  }, [isOAuthLoading, isOAuthValidated, oauthError, deniedSignedOut, startOAuth]);

  // WCO-2914: sign-out from the access-denied screen is terminal — show a signed-out screen with a
  // manual sign-in instead of auto-redirecting. Checked first: after logout() settles the gate is
  // simply "unvalidated", which every branch below would otherwise treat as "go sign in".
  if (deniedSignedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <ShieldOff className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              {t('oauth.signedOut.title')}
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              {t('oauth.signedOut.message')}
            </p>
            {/* startOAuth() is a full-page redirect, so deniedSignedOut needs no reset — resetting it
                first would let the auto-redirect effect fire a second startOAuth(). */}
            <button
              onClick={startOAuth}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
            >
              {t('oauth.signedOut.signIn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - validating token
  if (isOAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">
            {t('oauth.validating')}
          </p>
        </div>
      </div>
    );
  }

  // Error state - show error with retry button
  if (oauthError) {
    const isSessionExpired = oauthError === 'sessionExpired';

    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              {t('oauth.error')}
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              {isSessionExpired ? t('oauth.sessionExpired') : oauthError}
            </p>
            <button
              onClick={clearOAuthAndRestart}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              {t('oauth.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Redirecting state - user is being redirected to OAuth
  if (!isOAuthValidated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">
            {t('oauth.redirecting')}
          </p>
        </div>
      </div>
    );
  }

  // WCO-2914: signed in on an authorized domain, but not granted HTE access. Block the WHOLE app
  // (every page, including the home page) with a clear message instead of letting them in. Only
  // reachable through Google sign-in: shop-floor devices run the edge build with OAuth off, which
  // returns above and never gets here. Only when enforcement is switched on; the gateway
  // independently re-checks every /api call regardless of this screen.
  if (HTE_ACCESS_ENFORCE && !hasHteAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <ShieldOff className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              {t('oauth.accessDenied.title')}
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-2">
              {t('oauth.accessDenied.message')}
            </p>
            {oauthUser?.email && (
              <p className="text-sm text-secondary-500 dark:text-secondary-500 mb-6 break-all">
                {oauthUser.email}
              </p>
            )}
            <button
              onClick={() => {
                setDeniedSignedOut(true);
                // logout() clears client state in its finally; a failed server call must not surface
                // as an unhandled rejection (same handling as useAuth's sign-out).
                logout().catch(() => {});
              }}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
            >
              {t('oauth.accessDenied.signOut')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Validated - render the application
  return <>{children}</>;
};

export default OAuthGate;
